import * as xlsx from 'xlsx';
import {
  DIAGRAM_SPREADSHEET_IMPORT_TYPES,
  EMPTY_VISUAL_MEASUREMENT_VALUE,
} from './diagramImportConstants';
import { formatDiagramImportValue } from './diagramImportHelpers';

const SPREADSHEET_COLUMN_KEYS = [
  'label',
  'bubble type',
  'description',
  'unit',
  'expected value',
  'tolerance 1',
  'tolerance 2',
  'positions',
  'orientation',
];

function getColumnIndexes(headers) {
  return SPREADSHEET_COLUMN_KEYS.reduce((indexes, key) => {
    indexes[key] = headers.indexOf(key);
    return indexes;
  }, {});
}

function hasValidSpreadsheetHeaders(indexes) {
  return SPREADSHEET_COLUMN_KEYS.every((key) => indexes[key] !== -1);
}

function isEmptySpreadsheetRow(row, indexes) {
  return SPREADSHEET_COLUMN_KEYS.every((key) => !row[indexes[key]]);
}

function buildImportedBubbleFromRow(row, indexes, match, formatValue) {
  const bubbleTypeCell = row[indexes['bubble type']];
  let unitValue = '';
  let genericTol = false;

  const type = bubbleTypeCell === 'aligned length' ? 'length' : bubbleTypeCell;

  if (match.type !== type && match.type === 'length') {
    match.firstPoint = { x: '', y: '' };
    match.secondPoint = { x: '', y: '' };
    match.thirdPoint = { x: '', y: '' };
    match.fourthPoint = { x: '', y: '' };
  } else if (match.type !== type && type === 'length') {
    match.firstPoint = match.point;
    match.secondPoint = match.point;
    match.thirdPoint = match.point;
    match.fourthPoint = match.point;
  }

  let expectedValueLoc = row[indexes['expected value']];
  let tolerance1Loc = row[indexes['tolerance 1']];
  let tolerance2Loc = row[indexes['tolerance 2']];

  if (
    bubbleTypeCell === 'generic' &&
    (row[indexes['expected value']] >= 0.0 ||
      row[indexes['tolerance 1']] >= 0.0 ||
      row[indexes['tolerance 2']] >= 0.0)
  ) {
    expectedValueLoc = formatValue(row[indexes['expected value']]);
    tolerance1Loc = formatValue(row[indexes['tolerance 1']]);
    tolerance2Loc = formatValue(row[indexes['tolerance 2']]);
    genericTol = true;
  } else if (
    bubbleTypeCell === 'runout' &&
    (row[indexes['tolerance 1']] === undefined ||
      row[indexes['tolerance 1']] === '')
  ) {
    tolerance1Loc = '0.0000';
  } else if (
    bubbleTypeCell === 'diameter' ||
    bubbleTypeCell === 'length' ||
    bubbleTypeCell === 'aligned length'
  ) {
    expectedValueLoc = formatValue(row[indexes['expected value']]);
    tolerance1Loc = formatValue(row[indexes['tolerance 1']]);
    tolerance2Loc = formatValue(row[indexes['tolerance 2']]);
  }

  if (bubbleTypeCell === 'visual') {
    unitValue = '';
  } else if (
    (row[indexes.unit] === 'in' ||
      row[indexes.unit] === 'inch' ||
      row[indexes.unit] === undefined) &&
    bubbleTypeCell !== 'visual'
  ) {
    unitValue = 'in';
  } else {
    unitValue = row[indexes.unit];
  }

  return {
    number: row[indexes.label],
    type: bubbleTypeCell === 'aligned length' ? 'length' : bubbleTypeCell,
    lengthType: bubbleTypeCell === 'aligned length' ? 'aligned' : '',
    description: row[indexes.description] || '',
    unit: unitValue,
    expectedValue: expectedValueLoc || '',
    toleranceType: 'normal',
    tolerance: bubbleTypeCell === 'runout' ? tolerance1Loc : '',
    positiveTolerance: bubbleTypeCell !== 'runout' ? tolerance1Loc : '',
    negativeTolerance: tolerance2Loc || '',
    genericTolerance: genericTol,
    measurementCount:
      bubbleTypeCell === 'visual' ? 1 : row[indexes.positions] || 1,
    measurements:
      bubbleTypeCell === 'visual'
        ? [
            EMPTY_VISUAL_MEASUREMENT_VALUE,
            EMPTY_VISUAL_MEASUREMENT_VALUE,
            EMPTY_VISUAL_MEASUREMENT_VALUE,
            EMPTY_VISUAL_MEASUREMENT_VALUE,
          ]
        : [],
    multipleMeasurements:
      row[indexes.positions] > 1 && bubbleTypeCell !== 'visual',
    orientation: row[indexes.orientation] || 'up',
    hasDescription: !!row[indexes.description],
    descriptionDropdown: match.descriptionDropdown
      ? match.descriptionDropdown
      : 'other',
    point: match.point ? match.point : { x: '', y: '' },
    firstPoint: match.firstPoint ? match.firstPoint : { x: '', y: '' },
    secondPoint: match.secondPoint ? match.secondPoint : { x: '', y: '' },
    thirdPoint: match.thirdPoint ? match.thirdPoint : { x: '', y: '' },
    fourthPoint: match.fourthPoint ? match.fourthPoint : { x: '', y: '' },
  };
}

export default function parseDiagramImportSpreadsheet(
  binary,
  existingBubbles,
  { formatValue = formatDiagramImportValue, onInvalidHeader } = {}
) {
  const workbook = xlsx.read(binary, { type: 'binary' });
  const workSheetBubbles = xlsx.utils.sheet_to_json(
    workbook.Sheets[workbook.SheetNames[0]],
    { header: 1 }
  );

  if (!workSheetBubbles.length) {
    return [];
  }

  const bubbleHeaders = workSheetBubbles[0].map((header) =>
    header.toLowerCase()
  );
  workSheetBubbles.splice(0, 1);
  workSheetBubbles.sort((a, b) => a[0] - b[0]);

  const columnIndexes = getColumnIndexes(bubbleHeaders);
  if (!hasValidSpreadsheetHeaders(columnIndexes)) {
    if (onInvalidHeader) {
      onInvalidHeader();
    }
    return null;
  }

  const diagramData = [];

  workSheetBubbles.forEach((row) => {
    let match = existingBubbles.find(
      (item) => item.number === row[columnIndexes.label]
    );
    if (!match) {
      match = [];
    }

    const bubbleType = (row[columnIndexes['bubble type']] || '').toLowerCase();
    if (
      isEmptySpreadsheetRow(row, columnIndexes) ||
      !DIAGRAM_SPREADSHEET_IMPORT_TYPES.includes(bubbleType)
    ) {
      return;
    }

    diagramData.push(
      buildImportedBubbleFromRow(row, columnIndexes, match, formatValue)
    );
  });

  return diagramData;
}
